import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { NumberRow } from "@/components/NumberPill";
import { GameLogo } from "@/components/GameLogo";
import GameFaqSection from "@/components/GameFaqSection";
import { RelatedSections } from "@/components/RelatedSections";
import { relatedGamesForGame, relatedStatesForGame } from "@/lib/related-links";
import { useGameBySlug, useResults, useStateBySlug } from "@/hooks/use-query-hooks";
import { formatDate } from "@/lib/format";
import { ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, History } from "lucide-react";
import { RouteNotFound } from "@/components/RouteNotFound";
import { DrawJsonLd } from "@/components/DrawJsonLd";
import { gamePaths } from "@/lib/game-routing";
import { drawNavigation, findRouteGame, resultFaqs } from "@/lib/results-nav";
import { STATES } from "@/data/states";
import { getResultsForGame } from "@/data/results";

const A = Link as unknown as React.ComponentType<Record<string, unknown>>;

function weekday(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" })
    : "";
}

export function GameResultDateView({
  gameSlug,
  date,
  stateSlug,
}: {
  gameSlug: string;
  date: string;
  stateSlug?: string;
}) {
  const paths = gamePaths(gameSlug, stateSlug);
  const { data: state } = useStateBySlug(stateSlug);
  const { data: game, isFetched: gameFetched, isLoading: gameLoading } = useGameBySlug(gameSlug, state?.id);
  const { data: results } = useResults(game?.id, 200);

  const staticGame = useMemo(() => findRouteGame(gameSlug, stateSlug), [gameSlug, stateSlug]);
  // Static fallback so the winning numbers are present in the SSR HTML that
  // crawlers read, before the live query hydrates.
  const staticRows = useMemo(
    () => (staticGame ? getResultsForGame(staticGame.id, 200) : []),
    [staticGame],
  );
  const rows: any[] = (results && results.length ? results : staticRows) as any[];
  const match: any = rows.find((r: any) => r.draw_date === date);

  const nav = useMemo(
    () => (staticGame ? drawNavigation(staticGame.id, date) : { prev: null, next: null, recent: [], total: 0, latest: null }),
    [staticGame, date],
  );

  const gameName = game?.name ?? staticGame?.name ?? gameSlug;
  const staticStateName = stateSlug ? STATES.find((s) => s.slug === stateSlug)?.name ?? null : null;
  const stateName = (game as any)?.states?.name ?? state?.name ?? staticStateName;
  // Avoid "Texas Texas Lotto" when the game name already carries the state.
  const scope = stateName && !gameName.toLowerCase().startsWith(stateName.toLowerCase())
    ? `${stateName} ${gameName}`
    : gameName;
  const dateLabel = formatDate(date);

  const faqs = useMemo(
    () =>
      resultFaqs({
        gameName,
        dateLabel,
        drawSchedule: game?.draw_schedule ?? staticGame?.draw_schedule ?? null,
        stateName,
        numbers: match?.winning_numbers ?? [],
        bonus: match?.bonus_numbers ?? [],
        multiplier: match?.multiplier ?? null,
      }),
    [gameName, dateLabel, game?.draw_schedule, staticGame?.draw_schedule, stateName, match],
  );

  const relatedGames = useMemo(() => relatedGamesForGame(gameSlug, stateSlug, 6), [gameSlug, stateSlug]);
  const relatedStatesLinks = useMemo(
    () => relatedStatesForGame(gameSlug, !stateSlug, stateSlug, 6),
    [gameSlug, stateSlug],
  );

  if (!gameLoading && gameFetched && !game) {
    return (
      <Layout>
        <RouteNotFound gameSlug={gameSlug} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-6 md:py-10">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            {stateSlug && (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <A to="/states/$stateSlug" params={{ stateSlug }}>{stateName ?? stateSlug}</A>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
            <BreadcrumbItem>
              <BreadcrumbLink asChild><A to={paths.game}>{gameName}</A></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild><A to={paths.history}>Results history</A></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{dateLabel}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">
            <div className="flex items-start gap-4 mb-4">
              {(game || staticGame) && (
                <GameLogo
                  gameSlug={(game?.slug ?? staticGame?.slug) as string}
                  gameName={gameName}
                  logoUrl={game?.logo_url ?? staticGame?.logo_url}
                  size="md"
                />
              )}
              <div className="min-w-0">
                <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight">
                  {scope} winning numbers for {dateLabel}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4" aria-hidden="true" />
                  {weekday(date)} drawing · Result published on USA Lottery Club
                </p>
              </div>
            </div>

            {match ? (
              <>
                <DrawJsonLd
                  draws={[
                    {
                      gameName,
                      gameSlug,
                      drawDate: date,
                      winningNumbers: match.winning_numbers ?? [],
                      bonusNumbers: match.bonus_numbers ?? [],
                      multiplier: match.multiplier ?? null,
                      stateName,
                      url: `https://usalotteryclub.com${paths.result(date)}`,
                    },
                  ]}
                />
                <Card className="border-primary/30 shadow-sm">
                  <CardContent className="py-6">
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                      Winning numbers — {dateLabel}
                    </h2>
                    <NumberRow
                      numbers={match.winning_numbers || []}
                      bonusNumbers={match.bonus_numbers || []}
                      multiplier={match.multiplier}
                      size="lg"
                      align="left"
                    />
                    <p className="sr-only">
                      {scope} winning numbers on {dateLabel}: {(match.winning_numbers || []).join(", ")}
                      {(match.bonus_numbers || []).length ? `, bonus ${(match.bonus_numbers || []).join(", ")}` : ""}
                      {match.multiplier ? `, multiplier ${match.multiplier}x` : ""}.
                    </p>
                  </CardContent>
                </Card>

                <section className="mt-8" aria-labelledby="draw-details-heading">
                  <h2 id="draw-details-heading" className="font-sans text-[20px] font-bold mb-3 tracking-tight">
                    Draw details
                  </h2>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium w-40">Game</TableCell>
                        <TableCell>{scope}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Draw date</TableCell>
                        <TableCell>{dateLabel} ({weekday(date)})</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Winning numbers</TableCell>
                        <TableCell>{(match.winning_numbers || []).join(", ") || "—"}</TableCell>
                      </TableRow>
                      {(match.bonus_numbers || []).length > 0 && (
                        <TableRow>
                          <TableCell className="font-medium">Bonus</TableCell>
                          <TableCell>{(match.bonus_numbers || []).join(", ")}</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell className="font-medium">Multiplier</TableCell>
                        <TableCell>{match.multiplier ? `${match.multiplier}x` : "Not applicable"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Draw schedule</TableCell>
                        <TableCell>{game?.draw_schedule ?? staticGame?.draw_schedule ?? "See game page"}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Always confirm a winning ticket against the official lottery before claiming a prize.{" "}
                    <Link to="/tools/results-checker" className="text-primary hover:underline">
                      Check your ticket numbers
                    </Link>{" "}
                    or{" "}
                    <Link to="/tools/tax-calculator" className="text-primary hover:underline">
                      estimate the payout after tax
                    </Link>
                    .
                  </p>
                </section>
              </>
            ) : (
              <div className="text-muted-foreground">
                <p className="mb-4">No {gameName} drawing was found for {dateLabel}.</p>
                <RouteNotFound
                  gameSlug={gameSlug}
                  title="No draw on this date"
                  description="This game wasn't drawn on the requested date. Browse the full history or check the latest winning numbers."
                />
              </div>
            )}

            <nav
              className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4"
              aria-label={`${gameName} draw navigation`}
            >
              {nav.prev ? (
                <A
                  to={paths.result(nav.prev)}
                  rel="prev"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" /> {gameName} winning numbers for {formatDate(nav.prev)}
                </A>
              ) : (
                <span />
              )}
              <A to={paths.history} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <History className="h-4 w-4" aria-hidden="true" /> All {gameName} results
              </A>
              {nav.next ? (
                <A
                  to={paths.result(nav.next)}
                  rel="next"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  {gameName} winning numbers for {formatDate(nav.next)} <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </A>
              ) : (
                <span />
              )}
            </nav>

            {nav.recent.length > 1 && (
              <section className="mt-8" aria-labelledby="recent-draws-heading">
                <h2 id="recent-draws-heading" className="font-sans text-[20px] font-bold mb-3 tracking-tight">
                  Recent {gameName} winning numbers
                </h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Draw date</TableHead>
                      <TableHead>Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nav.recent.map((d) => {
                      const row: any = rows.find((r: any) => r.draw_date === d);
                      return (
                        <TableRow key={d} className={d === date ? "bg-muted/50" : undefined}>
                          <TableCell className="whitespace-nowrap">
                            {d === date ? (
                              <span className="font-semibold">{formatDate(d)}</span>
                            ) : (
                              <A
                                to={paths.result(d)}
                                className="text-primary hover:underline"
                                title={`${gameName} winning numbers for ${formatDate(d)}`}
                              >
                                {gameName} winning numbers for {formatDate(d)}
                              </A>
                            )}
                          </TableCell>
                          <TableCell>
                            {row ? (
                              <NumberRow
                                numbers={row.winning_numbers || []}
                                bonusNumbers={row.bonus_numbers || []}
                                multiplier={row.multiplier}
                                size="sm"
                                align="left"
                                nowrap
                              />
                            ) : (
                              <span className="text-muted-foreground text-sm">View result</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </section>
            )}

            <GameFaqSection
              faqs={faqs}
              gameSlug={gameSlug}
              stateSlug={stateSlug}
              heading={`${scope} ${dateLabel} — questions players ask`}
            />

            <RelatedSections
              games={relatedGames}
              states={relatedStatesLinks}
              gamesHeading="Other lottery results to compare"
              statesHeading="State lottery results hubs"
              gameSlug={gameSlug}
              stateSlug={stateSlug}
            />
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
            <Card>
              <CardContent className="py-5">
                <h2 className="font-sans text-base font-bold mb-2">Jump to</h2>
                <ul className="space-y-2 text-sm">
                  <li>
                    <A to={paths.game} className="text-primary hover:underline">
                      Latest {gameName} numbers
                    </A>
                  </li>
                  <li>
                    <A to={paths.history} className="text-primary hover:underline">
                      {gameName} results archive{nav.total ? ` (${nav.total} draws)` : ""}
                    </A>
                  </li>
                  {nav.latest && nav.latest !== date && (
                    <li>
                      <A to={paths.result(nav.latest)} className="text-primary hover:underline">
                        Most recent draw: {formatDate(nav.latest)}
                      </A>
                    </li>
                  )}
                  <li>
                    <Link to="/results/$date" params={{ date }} className="text-primary hover:underline">
                      Every game drawn on {dateLabel}
                    </Link>
                  </li>
                  {stateSlug && (
                    <li>
                      <A to="/states/$stateSlug" params={{ stateSlug }} className="text-primary hover:underline">
                        All {stateName ?? stateSlug} lottery games
                      </A>
                    </li>
                  )}
                  <li>
                    <Link to="/results" className="text-primary hover:underline">
                      Today&apos;s lottery results
                    </Link>
                  </li>
                  <li>
                    <Link to="/tools/frequency-analyzer" className="text-primary hover:underline">
                      Hot &amp; cold number analysis
                    </Link>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-5">
                <A to={paths.history} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to full history
                </A>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
